import { useEffect } from 'react';
import { useFlags } from '@atlaskit/flag/use-flags';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRovoEntitlement } from '../clients/gateway-client/gatewayClient';
import { useSite } from './useSite';
import { broadcastEntitlementToExtension } from '../utils/siteHelpers';
import { messages } from './messages';

const errorState = {
	shownFlag: false,
};

export const useEntitlementCheck = (): { isRovoLLMEnabled: boolean; isLoading: boolean } => {
	const { site } = useSite();
	const { showFlag } = useFlags();
	const queryClient = useQueryClient();
	const { data: rovoEntitlement, error, isLoading } = useRovoEntitlement(site.workspace.cloudId);
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (error && !errorState.shownFlag) {
			errorState.shownFlag = true;
			showFlag({
				title: formatMessage(messages.entitlementCheckFailedFlagTitle),
				description: formatMessage(messages.entitlementCheckFailedFlagDescription),
				appearance: 'error',
				actions: [
					{
						content: formatMessage(messages.entitlementCheckFailedFlagAction),
						onClick: async (): Promise<void> => {
							await queryClient.invalidateQueries({
								queryKey: ['rovoEntitlement', site.workspace.cloudId],
							});
							window.location.reload();
						},
					},
				],
			});
		}
	}, [error, showFlag, queryClient, site.workspace.cloudId, formatMessage]);

	useEffect(() => {
		if (rovoEntitlement) {
			broadcastEntitlementToExtension(rovoEntitlement.isRovoLLMEnabled);
		}
	}, [rovoEntitlement]);

	return {
		isRovoLLMEnabled: rovoEntitlement?.isRovoLLMEnabled ?? false,
		isLoading,
	};
};
